"use client";

import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import {
    X,
    ArrowRight,
    Sparkles,
    ShieldCheck,
    Leaf,
    Droplets,
    Atom,
    Package,
    ArrowLeft,
    CheckCircle2,
    Factory,
    Globe2,
    FlaskConical,
    FileCheck2
} from "lucide-react";
import NextImage from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import ContactForm from "./ContactForm";
import { DocumentList } from "./DocumentList";
import { PageSkeleton } from "@/components/ui/Skeleton";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ThemeProvider } from "@/components/ThemeProvider";

interface ProductDetailProps {
    slug: string;
    isModal?: boolean;
    onClose?: () => void;
    initialProduct?: any;
    showHeaderFooter?: boolean;
    showSpecs?: boolean;
    showTrust?: boolean;
}

export default function ProductDetail({
    slug,
    isModal = false,
    onClose,
    initialProduct,
    showHeaderFooter = false,
    showSpecs = true,
    showTrust = true
}: ProductDetailProps) {
    const router = useRouter();
    const liveProduct = useQuery(api.products.getBySlug, { slug });
    const product = liveProduct !== undefined ? liveProduct : initialProduct;

    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const scrollLeft = scrollContainerRef.current.scrollLeft;
            const width = scrollContainerRef.current.clientWidth;
            const newIndex = Math.round(scrollLeft / width);
            if (newIndex !== activeImageIndex) {
                setActiveImageIndex(newIndex);
            }
        }
    };

    const handleInquiry = () => {
        setIsDrawerOpen(true);
    };

    if (product === undefined) {
        return (
            <div className="min-h-screen bg-[var(--nb-colors-background)] flex items-center justify-center">
                <div className="animate-pulse text-nb-green font-bold">Loading Product...</div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-[var(--nb-colors-background)] flex flex-col items-center justify-center p-4">
                <h1 className="text-2xl font-bold text-slate-900 mb-4">Product Not Found</h1>
                <Link href="/products" className="text-nb-green hover:underline">Back to Catalog</Link>
            </div>
        );
    }

    const technicalSpecs = [
        { label: 'Purity Level', value: '100% Organic Extracts', icon: <Sparkles className="w-5 h-5" /> },
        { label: 'Quality Standard', value: 'GMP & ISO Certified', icon: <ShieldCheck className="w-5 h-5" /> },
        { label: 'Source', value: product.botanicalName || 'Sustainably Farmed', icon: <Leaf className="w-5 h-5" /> },
        { label: 'Process', value: product.extractionMethod || 'Cold Press Extraction', icon: <Droplets className="w-5 h-5" /> },
        { label: 'Active Compounds', value: product.activeCompounds || 'Rich in Antioxidants', icon: <Atom className="w-5 h-5" /> },
        { label: 'Customizable MOQ', value: `${product.moq || 500} Units`, icon: <Package className="w-5 h-5" /> },
    ];

    const complianceSteps = [
        { title: "R&D Validation", desc: "Scientific formulation testing in our state-of-the-art labs.", icon: <FlaskConical className="w-5 h-5" /> },
        { title: "Quality Control", desc: "Multi-stage inspection protocols ensuring zero-defect output.", icon: <FileCheck2 className="w-5 h-5" /> },
        { title: "Global Shipping", desc: "Secure logistics handling for international B2B distributions.", icon: <Globe2 className="w-5 h-5" /> }
    ];

    const images = (product.images || []).filter((img: any) => typeof img === 'string' && img !== "[object Object]");
    const hasMultipleImages = images.length > 1;

    const scrollToImage = (index: number) => {
        if (scrollContainerRef.current) {
            const width = scrollContainerRef.current.clientWidth;
            scrollContainerRef.current.scrollTo({
                left: width * index,
                behavior: 'smooth'
            });
            setActiveImageIndex(index);
        }
    };

    const content = (
        <div className="bg-[var(--nb-colors-background)] min-h-screen flex flex-col font-sans">
            {showHeaderFooter && <SiteHeader />}
            <main className={`flex-grow ${showHeaderFooter ? 'pb-24 md:pb-20' : 'pb-12'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">

                        {/* LEFT: Image Section */}
                        <div className="lg:col-span-5 space-y-4">
                            <div className="aspect-[4/5] sm:aspect-square rounded-[32px] sm:rounded-[40px] overflow-hidden bg-white border border-slate-900/5 shadow-2xl shadow-slate-200/50 relative group">
                                <div className="absolute inset-0 bg-gradient-to-br from-nb-green/5 to-transparent opacity-50 z-0 pointer-events-none" />

                                {images.length > 0 ? (
                                    <div
                                        ref={scrollContainerRef}
                                        onScroll={handleScroll}
                                        className="flex h-full w-full overflow-x-auto snap-x snap-mandatory scrollbar-none [scrollbar-width:none] [-ms-overflow-style:none] touch-pan-x"
                                    >
                                        {images.map((img: string, i: number) => (
                                            <div key={i} className="relative flex-shrink-0 w-full h-full snap-start snap-always">
                                                <NextImage
                                                    src={img.startsWith('http') ? img : `/api/storage/${img}`}
                                                    fill
                                                    className="object-cover relative z-10 transition-transform duration-[2s]"
                                                    alt={`${product.name} - Image ${i + 1}`}
                                                    sizes="(max-width: 768px) 100vw, 40vw"
                                                    priority={i === 0}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-8xl opacity-10">🌿</div>
                                )}

                                {hasMultipleImages && (
                                    <>
                                        <button
                                            onClick={(e) => { e.preventDefault(); scrollToImage(Math.max(0, activeImageIndex - 1)); }}
                                            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-md border border-white flex items-center justify-center text-slate-700 hover:text-nb-green hover:bg-white transition-all opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 disabled:opacity-0 disabled:cursor-not-allowed"
                                            disabled={activeImageIndex === 0}
                                            aria-label="Previous image"
                                            title="Previous image"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="m15 18-6-6 6-6" /></svg>
                                        </button>
                                        <button
                                            onClick={(e) => { e.preventDefault(); scrollToImage(Math.min(images.length - 1, activeImageIndex + 1)); }}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-md border border-white flex items-center justify-center text-slate-700 hover:text-nb-green hover:bg-white transition-all opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 disabled:opacity-0 disabled:cursor-not-allowed"
                                            disabled={activeImageIndex === images.length - 1}
                                            aria-label="Next image"
                                            title="Next image"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="m9 18 6-6-6-6" /></svg>
                                        </button>
                                    </>
                                )}
                            </div>

                            {hasMultipleImages && (
                                <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 mt-4 px-1 pb-1 overflow-x-auto scrollbar-none snap-x snap-mandatory">
                                    {images.map((img: string, i: number) => (
                                        <button
                                            key={i}
                                            onClick={() => scrollToImage(i)}
                                            title={`Scroll to image ${i + 1}`}
                                            className={`snap-start relative aspect-square rounded-2xl overflow-hidden border-2 transition-all cursor-pointer ${i === activeImageIndex
                                                ? 'border-nb-green shadow-premium scale-[1.02]'
                                                : 'border-transparent hover:border-slate-200 opacity-60 hover:opacity-100'
                                                }`}
                                        >
                                            <NextImage
                                                src={img.startsWith('http') ? img : `/api/storage/${img}`}
                                                fill
                                                className="object-cover"
                                                alt={`Thumbnail ${i + 1}`}
                                                sizes="80px"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {showTrust && (
                                <div className="p-8 rounded-[32px] bg-slate-900 text-white relative overflow-hidden hidden lg:block">
                                    <div className="relative z-10">
                                        <h4 className="text-nb-green font-black uppercase tracking-[0.2em] text-[10px] mb-4">Manufacturer Trust</h4>
                                        <ul className="space-y-4">
                                            {complianceSteps.map((step, i) => (
                                                <li key={i} className="flex gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-nb-green shrink-0">
                                                        {step.icon}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-black">{step.title}</div>
                                                        <p className="text-xs text-slate-400 font-medium leading-relaxed">{step.desc}</p>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-nb-green/10 rounded-full blur-[60px] -mr-16 -mt-16" />
                                </div>
                            )}
                        </div>

                        {/* RIGHT: Content Section */}
                        <div className="lg:col-span-7 space-y-8">
                            <div className="space-y-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="px-3 py-1 bg-nb-green/10 text-nb-green text-[9px] font-black uppercase tracking-[0.2em] rounded-full border border-nb-green/20">
                                        {product.tags?.[0] || 'Exclusive Formulation'}
                                    </span>
                                </div>

                                <h1 className="text-3xl sm:text-5xl font-black text-slate-900 leading-[1.1] tracking-tight">
                                    {product.name}
                                </h1>

                                <div className="flex items-center gap-3 text-slate-400">
                                    <span className="text-[10px] font-black uppercase tracking-widest border-r border-slate-200 pr-3">SKU: {product.sku || 'N/A'}</span>
                                    <span className="text-[10px] font-black uppercase tracking-widest">Scientific Grade Output</span>
                                </div>
                            </div>

                            <div className="prose prose-slate max-w-none">
                                <h3 className="text-base font-black text-slate-900 mb-3 flex items-center gap-2">
                                    <CheckCircle2 className="text-nb-green" size={18} />
                                    Product Overview
                                </h3>
                                <p className="text-md sm:text-lg text-slate-600 leading-relaxed font-medium">
                                    {product.description}
                                </p>
                            </div>

                            <div className="hidden md:block">
                                <button
                                    onClick={handleInquiry}
                                    className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-bold text-white bg-nb-green rounded-full hover:shadow-xl hover:shadow-nb-green/30 transition-all hover:-translate-y-0.5 relative overflow-hidden group/btn"
                                >
                                    {/* Premium Sheen Effect */}
                                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                                        <div className="absolute inset-0 w-2/3 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-25deg] -translate-x-[150%] group-hover/btn:animate-premium-sheen" />
                                    </div>

                                    <span className="relative z-10">Inquire Now</span>
                                    <ArrowRight className="w-5 h-5 relative z-10" />
                                </button>
                            </div>

                            {showSpecs && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                                    {technicalSpecs.map((spec, idx) => (
                                        <div key={idx} className="p-5 rounded-[24px] bg-white border border-slate-100 hover:border-nb-green/30 transition-all hover:shadow-xl hover:shadow-nb-green/5 group flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-2xl bg-nb-green/5 flex items-center justify-center text-nb-green group-hover:scale-110 transition-transform shrink-0 shadow-sm border border-nb-green/10">
                                                {spec.icon}
                                            </div>
                                            <div>
                                                <h4 className="text-[10px] font-black uppercase text-nb-green/60 tracking-[0.2em] mb-1">{spec.label}</h4>
                                                <p className="text-xs sm:text-sm font-bold text-slate-900">{spec.value}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* FAQ & Ordering Info */}
                            <div className="space-y-4 pt-6">
                                <h3 className="text-lg font-black text-slate-900 mb-4 px-2 tracking-tight">Product FAQ & Ordering Info</h3>
                                {[
                                    {
                                        q: "What is the Minimum Order Quantity (MOQ)?",
                                        a: `Our standard MOQ for ${product.name} is ${product.moq || '500'} units. However, for initial trial orders or specific private label requirements, we may offer flexible quantities. Please inquire for details.`
                                    },
                                    {
                                        q: "What is the typical manufacturing lead time?",
                                        a: "Once the formulation and packaging are finalized, our standard production lead time is 15-25 business days. This can vary based on raw material availability and order volume."
                                    },
                                    {
                                        q: "Do you provide export and compliance documentation?",
                                        a: "Yes. As an ISO certified manufacturer, we provide all necessary documentation including Certificate of Analysis (COA), MSDS, and relevant quality certificates for global export."
                                    }
                                ].map((item, i) => (
                                    <details key={i} className="group p-6 rounded-[24px] bg-white border border-slate-100 hover:border-nb-green/30 transition-all">
                                        <summary className="flex items-center justify-between cursor-pointer font-bold text-slate-900 text-sm list-none">
                                            {item.q}
                                            <span className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-open:rotate-180 transition-transform">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                                            </span>
                                        </summary>
                                        <p className="mt-4 text-sm text-slate-500 font-medium leading-relaxed border-t border-slate-50 pt-4">
                                            {item.a}
                                        </p>
                                    </details>
                                ))}
                            </div>

                            {product.documents && product.documents.length > 0 && (
                                <div className="p-8 rounded-[32px] bg-white border border-slate-900/5 shadow-xl shadow-slate-200/50">
                                    <DocumentList documents={product.documents} title="Technical Data Sheets & Catalogs" />
                                </div>
                            )}

                            {showTrust && (
                                <div className="p-6 rounded-[24px] bg-slate-900 text-white lg:hidden">
                                    <h4 className="text-nb-green font-black uppercase tracking-[0.2em] text-[10px] mb-4">Our Manufacturing Standards</h4>
                                    <div className="space-y-4">
                                        {complianceSteps.map((step, i) => (
                                            <div key={i} className="flex gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-nb-green shrink-0">
                                                    {step.icon}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-black">{step.title}</div>
                                                    <p className="text-xs text-slate-400 font-medium leading-relaxed">{step.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-slate-200 z-40">
                <button
                    onClick={handleInquiry}
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 text-base font-bold text-white bg-nb-green rounded-full shadow-lg shadow-nb-green/30 relative overflow-hidden group/btn"
                >
                    {/* Premium Sheen Effect */}
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                        <div className="absolute inset-0 w-2/3 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-25deg] -translate-x-[150%] group-hover/btn:animate-premium-sheen" />
                    </div>

                    <span className="relative z-10">Inquire Now</span>
                    <ArrowRight className="w-5 h-5 relative z-10" />
                </button>
            </div>

            <div
                className={`fixed inset-0 z-50 transition-opacity duration-300 ${isDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            >
                <div
                    className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                    onClick={() => setIsDrawerOpen(false)}
                />
                <div
                    className={`absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
                >
                    <div className="p-6 md:p-8 flex-1 overflow-y-auto">
                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-nb-green border border-slate-200">
                                    <Package size={24} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black text-slate-900 leading-tight">B2B Inquiry</h2>
                                    <p className="text-nb-green text-xs font-bold uppercase tracking-wider">{product.name}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsDrawerOpen(false)}
                                className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"
                                title="Close Dialog"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <ContactForm
                            compact={true}
                            productId={product._id}
                            productName={product.name}
                            productCategory={product.tags?.[0]}
                            initialMessage={`I'm interested in wholesale options for ${product.name}. Please provide a technical quote and MOQ details for B2B distribution.`}
                        />
                    </div>
                </div>
            </div>

                <ShowcaseSection 
                    productId={product._id} 
                    categoryId={product.categoryId} 
                    showcaseTitle={product.showcaseTitle} 
                    showcaseProductIds={product.showcaseProductIds} 
                />
                {showHeaderFooter && <SiteFooter />}
            </div>
    );

    if (isModal) {
        return content;
    }

    return content;
}

function ShowcaseSection({ productId, categoryId, showcaseTitle, showcaseProductIds }: { productId: string, categoryId?: string, showcaseTitle?: string, showcaseProductIds?: string[] }) {
    const category = useQuery(api.categories.getById, categoryId ? { id: categoryId as any } : "skip");
    
    const finalIds = (showcaseProductIds && showcaseProductIds.length > 0) 
        ? showcaseProductIds 
        : (category?.defaultShowcaseProductIds && category?.defaultShowcaseProductIds.length > 0)
            ? category.defaultShowcaseProductIds
            : [];
            
    const finalTitle = showcaseTitle || category?.defaultShowcaseTitle || "Related Formulations";
    
    // Filter out current product
    const idsToFetch = finalIds.filter(id => id !== productId).slice(0, 4);
    
    const products = useQuery(api.products.getMany, idsToFetch.length > 0 ? { ids: idsToFetch as any } : "skip");

    if (!products || products.length === 0) return null;

    return (
        <section className="py-24 border-t border-slate-100 mt-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <div className="space-y-4">
                        <span className="text-nb-green font-black uppercase tracking-[0.3em] text-[10px] bg-nb-green/10 px-4 py-1.5 rounded-full border border-nb-green/20">Recommended For You</span>
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight leading-tight">{finalTitle}</h2>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {products.map((p: any) => p && (
                        <Link 
                            key={p._id} 
                            href={`/products/${p.slug}`}
                            className="group block"
                        >
                            <div className="relative aspect-square rounded-[32px] overflow-hidden bg-white border border-slate-100 shadow-sm transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-nb-green/10 group-hover:-translate-y-2">
                                {p.images?.[0] ? (
                                    <NextImage 
                                        src={p.images[0].startsWith('http') ? p.images[0] : `/api/storage/${p.images[0]}`}
                                        fill
                                        className="object-cover transition-transform duration-700 group-hover:scale-110"
                                        alt={p.name}
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-4xl opacity-10">🌿</div>
                                )}
                                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/60 to-transparent translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                                    <span className="inline-flex items-center gap-2 text-xs font-bold text-white">
                                        View Details <ArrowRight size={14} />
                                    </span>
                                </div>
                            </div>
                            <div className="mt-6 space-y-1 px-2">
                                <h3 className="text-lg font-black text-slate-900 group-hover:text-nb-green transition-colors line-clamp-1">{p.name}</h3>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{p.tags?.[0] || 'Exclusive Formulation'}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
